import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ChartOfAccountsSearch from "../ChartOfAccountsSearch";
import withholdingTaxAPI from "../../api/withholdingTaxAPI";


// Payee, ATC and employee pickers for withholding rows. The payee picker is a
// vendor search of its own (below). ATC and employee are thin wrappers around
// the same search field as the grid's "Description / COA" column
// (ChartOfAccountsSearch in its opt-in optionSource mode), so they look and
// behave like it — but both are PICK-ONLY (`strict`): typed text is never a
// value. A row with unmatched text stays incomplete.
//
// Why the ATC must be picked, never typed (docs/WHT_IMPLEMENTATION.md F3):
// 1601-EQ groups lines by ATC + rate, the 2307 prints the ATC per line, and
// the QAP DAT carries it per payee — a free-text code would reach all three
// unvalidated.

export const UnassignedBadge = () => (
  <span className="ml-1 text-xs px-1.5 py-0.5 rounded-md bg-ink-canvas text-ink-body border border-ink-line">
    Unassigned
  </span>
);

// ── ATC options: /atc-options for (client, payee type, year, month), cached
// for the page's life — a grid of 20 rows for one payee type asks once.
const atcCache = new Map();

export function loadAtcOptions(clientId, payeeType, year, month) {
  const key = `${clientId}|${payeeType}|${year}|${month}`;
  if (!atcCache.has(key)) {
    const p = withholdingTaxAPI.getAtcOptions(clientId, { payeeType, year, month })
      .then((res) => res?.data || [])
      .catch((err) => {
        atcCache.delete(key); // let a later search retry
        throw err;
      });
    atcCache.set(key, p);
  }
  return atcCache.get(key);
}

/** For tests. */
export const __resetAtcOptionCache = () => atcCache.clear();

/** 500 → "5%", 50 → "0.5%", 1250 → "12.5%". */
export const compactRate = (bp) => `${Number((bp / 100).toFixed(2))}%`;

/** Description without the reference data's " - Corporate" / " - Individual" suffix. */
export const shortAtcDescription = (d = "") => d.replace(/\s+-\s+(Corporate|Individual)\s*$/i, "").trim();

export const atcLabel = (o) =>
  [o.code, Number.isFinite(o.rateBasisPoints) ? compactRate(o.rateBasisPoints) : null, shortAtcDescription(o.description)]
    .filter(Boolean)
    .join(" · ");

/** Partial, case-insensitive match on code OR description ("158", "wc15", "professional", "rent"). */
export const matchesAtc = (o, query) => {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  return o.code.toLowerCase().includes(q) || String(o.description || "").toLowerCase().includes(q);
};

/**
 * value: the picked ATC option ({ code, rateBasisPoints, description }) or null.
 * Disabled until a payee type is known.
 */
export const AtcPicker = ({ clientId, payeeType, year, month, value, onChange, inputId, invalid = false, disabled = false, ariaLabel = "ATC" }) => {
  const ready = Boolean(clientId && payeeType && year && month);
  const source = useMemo(() => ({
    noun: "ATC",
    getKey: (o) => o.code,
    getDisplayValue: atcLabel,
    renderOption: (o) => (
      <>
        <div className="font-medium text-ink">{atcLabel(o)}</div>
        <div className="text-sm text-ink-body">{o.description}</div>
      </>
    ),
    search: async (q) => (ready ? (await loadAtcOptions(clientId, payeeType, year, month)).filter((o) => matchesAtc(o, q)) : []),
  }), [clientId, payeeType, year, month, ready]);

  return (
    <ChartOfAccountsSearch
      key={`${payeeType || "none"}`} // a new payee type starts a fresh field
      optionSource={source}
      strict
      keyboardSelect
      selectedOption={value || null}
      onSelect={(o) => onChange(o || null)}
      noMatchMessage="No matching ATC for this payee"
      placeholder={ready ? "Search ATC (code or description) *" : "Pick a payee first"}
      disabled={disabled || !ready}
      showLinkedBadge={false}
      inputId={inputId}
      ariaLabel={ariaLabel}
      invalid={invalid}
    />
  );
};

const payeeTinLabel = (p) => (p.profile?.tin9 ? `TIN ${p.profile.tin9}-${p.profile.tinBranch}` : "no TIN");

/**
 * Payee picker — a vendor search, not the COA field. Lists the client's own
 * vendors + the tenant's unassigned ones (D9 / D16) from /payees, searched by
 * name, code or TIN. Pick-only: typed text is never a value; editing after a
 * pick clears it. value: { _id, name, unassigned, profile } or null.
 *
 * Deliberately not the shared VendorSearch: that one searches every vendor in
 * the tenant (other clients' included) and returns no payee profile, which the
 * ATC picker needs for the payee type.
 */
export const PayeePicker = ({ clientId, value, onChange, inputId, invalid = false, disabled = false, ariaLabel = "Payee" }) => {
  const [query, setQuery] = useState(value?.name || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const seqRef = useRef(0);
  const blurTimer = useRef(null);
  // Set when typing un-picks the value, so the reset below keeps the text.
  const selfClearedRef = useRef(false);
  const listId = `${inputId || "payee"}-vendors`;

  // Controlled: a new value (or a cleared one) resets the text.
  useEffect(() => {
    if (selfClearedRef.current) { selfClearedRef.current = false; return; }
    setQuery(value?.name || "");
  }, [value?._id, value?.name]);
  useEffect(() => () => clearTimeout(blurTimer.current), []);

  const runSearch = useCallback(async (q) => {
    const seq = ++seqRef.current;
    if (!clientId) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await withholdingTaxAPI.searchPayees(clientId, q.trim());
      if (seq === seqRef.current) { setResults(res?.data || []); setHighlighted(0); }
    } catch {
      if (seq === seqRef.current) setResults([]);
    } finally {
      if (seq === seqRef.current) setSearching(false);
    }
  }, [clientId]);

  // Debounced search while the list is open and nothing is picked.
  useEffect(() => {
    if (!open || value) return undefined;
    const t = setTimeout(() => runSearch(query), 200);
    return () => clearTimeout(t);
  }, [query, open, value, runSearch]);

  const pick = (p) => {
    setQuery(p.name);
    setOpen(false);
    onChange(p);
  };

  const handleChange = (e) => {
    setQuery(e.target.value);
    setOpen(true);
    if (value) { selfClearedRef.current = true; onChange(null); } // pick-only: editing un-picks
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setOpen(true);
      const n = results.length;
      if (n) setHighlighted((i) => (e.key === "ArrowDown" ? (i + 1) % n : (i - 1 + n) % n));
    } else if (e.key === "Enter") {
      if (open && results.length) {
        e.preventDefault();
        pick(results[Math.min(highlighted, results.length - 1)]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const clear = () => {
    setQuery("");
    setResults([]);
    onChange(null);
  };

  const unmatched = !value && query.trim() && !open && !disabled;

  return (
    <div className="space-y-1">
      <div className="relative">
        <input
          type="text"
          id={inputId}
          aria-label={ariaLabel}
          aria-invalid={invalid || undefined}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listId}
          autoComplete="off"
          value={query}
          disabled={disabled}
          placeholder="Search vendor (name, code or TIN) *"
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => { clearTimeout(blurTimer.current); if (!value) setOpen(true); }}
          onBlur={() => { blurTimer.current = setTimeout(() => setOpen(false), 150); }}
          className={`w-full border rounded px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${
            disabled ? "bg-ink-canvas text-ink-body" : "bg-white"
          } ${invalid ? "border-danger-500" : "border-ink-line"}`}
        />

        {value && !disabled && (
          <button
            type="button"
            onClick={clear}
            aria-label="Clear payee"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-body hover:text-ink"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {open && (
          <div
            id={listId}
            role="listbox"
            className="absolute z-50 top-full w-full mt-1 bg-white border border-ink-line rounded-md shadow-lg max-h-72 overflow-y-auto"
          >
            {searching && !results.length ? (
              <div className="px-4 py-3 text-center text-sm text-ink-body">Searching vendors…</div>
            ) : results.length ? (
              <>
                <div className="px-4 py-2 bg-ink-canvas border-b border-ink-line text-xs font-medium text-ink-body sticky top-0">
                  {query.trim() ? `Found ${results.length} vendor(s) for "${query.trim()}"` : `${results.length} vendor(s)`}
                </div>
                {results.map((p, i) => (
                  <div
                    key={p._id}
                    role="option"
                    aria-selected={i === highlighted}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick(p)}
                    className={`px-4 py-2 cursor-pointer border-b border-ink-line last:border-b-0 hover:bg-primary-50 ${
                      i === highlighted ? "bg-primary-50" : ""
                    }`}
                  >
                    <div className="font-medium text-ink">
                      {p.name}
                      {p.unassigned && <UnassignedBadge />}
                    </div>
                    <div className="text-sm text-ink-body">
                      {p.profile?.payeeType === "individual" ? "Individual" : "Non-individual"}
                      {` • ${payeeTinLabel(p)}`}
                      {p.code ? ` • ${p.code}` : ""}
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <div className="px-4 py-3 text-center text-sm text-ink-body">
                {query.trim() ? "No matching vendor — add the vendor first" : "Type to search vendors"}
              </div>
            )}
          </div>
        )}
      </div>

      {value && (
        <div className="text-xs text-ink-body">
          {payeeTinLabel(value)}
          {value.unassigned && <UnassignedBadge />}
        </div>
      )}
      {unmatched && <p role="alert" className="text-xs text-danger-500">No matching vendor — add the vendor first</p>}
    </div>
  );
};

// ── Employees (F7a): the client's active employees, cached per client for the
// page's life; the Employees panel invalidates the cache after a change.
const employeeCache = new Map();

export function loadEmployees(clientId) {
  if (!employeeCache.has(clientId)) {
    const p = withholdingTaxAPI.listEmployees(clientId)
      .then((res) => res?.data || [])
      .catch((err) => {
        employeeCache.delete(clientId);
        throw err;
      });
    employeeCache.set(clientId, p);
  }
  return employeeCache.get(clientId);
}

export const invalidateEmployees = (clientId) => employeeCache.delete(clientId);
/** For tests. */
export const __resetEmployeeCache = () => employeeCache.clear();

export const employeeName = (e) =>
  e ? [`${e.lastName}, ${e.firstName}`, e.middleName].filter(Boolean).join(" ") : "";

/** Partial, case-insensitive match on last / first / middle name or TIN digits. */
export const matchesEmployee = (e, query) => {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  const digits = q.replace(/\D/g, "");
  return employeeName(e).toLowerCase().includes(q) || `${e.firstName} ${e.lastName}`.toLowerCase().includes(q)
    || (digits.length >= 3 && String(e.tin || "").includes(digits));
};

/**
 * Employee picker for COMP rows — pick-only, same field as Payee / ATC.
 * value: { _id, lastName, firstName, middleName, tin, isMwe } or null.
 */
export const EmployeePicker = ({ clientId, value, onChange, inputId, invalid = false, disabled = false, ariaLabel = "Employee" }) => {
  const source = useMemo(() => ({
    noun: "employee",
    getKey: (e) => e._id,
    getDisplayValue: employeeName,
    renderOption: (e) => (
      <>
        <div className="font-medium text-ink">
          {employeeName(e)}
          {e.isMwe && <span className="ml-1 text-xs px-1.5 py-0.5 rounded-md bg-primary-50 text-primary-700">MWE</span>}
        </div>
        <div className="text-sm text-ink-body">{e.tin ? `TIN ${e.tin}` : "no TIN"}</div>
      </>
    ),
    search: async (q) => (clientId ? (await loadEmployees(clientId)).filter((e) => matchesEmployee(e, q)) : []),
  }), [clientId]);

  return (
    <ChartOfAccountsSearch
      optionSource={source}
      strict
      keyboardSelect
      selectedOption={value || null}
      onSelect={(e) => onChange(e || null)}
      noMatchMessage="No matching employee — add them in the Employees panel"
      placeholder="Search employee (name or TIN) *"
      disabled={disabled}
      showLinkedBadge={false}
      inputId={inputId}
      ariaLabel={ariaLabel}
      invalid={invalid}
    />
  );
};
